import React, { useState, useEffect } from 'react';
import axios from 'axios';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editEventId, setEditEventId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  // Get the user's role and token from localStorage
  const role = localStorage.getItem('role') || 'student';
  const token = localStorage.getItem('token');

  // Fetch calendar events from the backend
  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/calendar`);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      alert('You must be logged in as a teacher to add or edit an event.');
      return;
    }
    if (!formData.title.trim() || !formData.date.trim()) {
      alert('Title and date are required.');
      return;
    }
    setLoading(true);
    try {
      if (editMode) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/calendar/${editEventId}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Event updated successfully!');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/calendar`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Event added successfully!');
      }
      setFormData({ title: '', date: '', description: '' });
      setEditMode(false);
      setEditEventId(null);
      setShowAddForm(false);
      fetchEvents();
    } catch (error) {
      console.error('Error saving calendar event:', error);
      alert('Error saving event.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setFormData({
      title: event.title,
      date: event.date.split('T')[0], // Ensure correct date format
      description: event.description,
    });
    setEditMode(true);
    setEditEventId(event._id);
    setShowAddForm(true);
  };

  const handleDelete = async (eventId) => {
    if (!token) {
      alert('You must be logged in as a teacher to delete an event.');
      return;
    }
    if (window.confirm('Are you sure you want to delete this event?')) {
      setLoading(true);
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/calendar/${eventId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        alert('Event deleted successfully!');
        fetchEvents();
      } catch (error) {
        console.error('Error deleting calendar event:', error);
        alert('Error deleting event.');
      } finally {
        setLoading(false);
      }
    }
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    return events
      .filter(event => new Date(event.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .slice(0, 3);
  };

  const isEventToday = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    return eventDate.toDateString() === today.toDateString();
  };

  const isEventUpcoming = (dateString) => {
    const eventDate = new Date(dateString);
    const today = new Date();
    const daysDiff = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    return daysDiff >= 0 && daysDiff <= 7;
  };

  return (
    <div className="calendar-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .calendar-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .calendar-container::before {
          content: '';
          position: absolute;
          top: 10%;
          left: 5%;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: float 4s ease-in-out infinite;
        }

        .calendar-container::after {
          content: '';
          position: absolute;
          bottom: 25%;
          right: 8%;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 3.5s ease-in-out infinite;
          animation-delay: 1.5s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(8deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes eventSlide {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .pulse-element {
          position: absolute;
          top: 65%;
          left: 12%;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.06);
          animation: pulse 2.5s ease-in-out infinite;
        }

        .calendar-wrapper {
          max-width: 1000px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1.1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .events-count {
          color: rgba(255, 255, 255, 0.9);
          font-size: 1rem;
          font-weight: 500;
        }

        .add-event-btn {
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 50px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          box-shadow: 0 4px 15px rgba(254, 107, 139, 0.3);
        }

        .add-event-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(254, 107, 139, 0.4);
        }

        .upcoming-events {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .upcoming-title {
          color: white;
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .upcoming-list {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .upcoming-item {
          background: rgba(255, 255, 255, 0.9);
          padding: 0.75rem 1rem;
          border-radius: 12px;
          font-size: 0.9rem;
          color: #374151;
          font-weight: 500;
          backdrop-filter: blur(10px);
        }

        .form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2.5rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .form-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #FE6B8B, #FF8E53, #4ECDC4, #667eea);
          border-radius: 20px 20px 0 0;
        }

        .form-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .form-icon {
          width: 32px;
          height: 32px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.9rem;
        }

        .form-input {
          width: 100%;
          padding: 1rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          box-sizing: border-box;
          resize: vertical;
        }

        .form-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .form-textarea {
          min-height: 100px;
          font-family: inherit;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .submit-btn {
          flex: 1;
          min-width: 150px;
          padding: 1rem 2rem;
          background: linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(254, 107, 139, 0.4);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .cancel-btn {
          padding: 1rem 2rem;
          background: rgba(107, 114, 128, 0.1);
          color: #374151;
          border: 2px solid rgba(107, 114, 128, 0.2);
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .cancel-btn:hover {
          background: rgba(107, 114, 128, 0.15);
          border-color: rgba(107, 114, 128, 0.3);
        }

        .events-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 1.5rem;
        }

        .event-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 18px;
          padding: 1.8rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          transition: all 0.3s ease;
          animation: eventSlide 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .event-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.15);
        }

        .event-card.today::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #10b981, #059669);
        }

        .event-card.upcoming::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 4px;
          height: 100%;
          background: linear-gradient(180deg, #f59e0b, #d97706);
        }

        .event-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 1rem;
          gap: 1rem;
        }

        .event-main {
          flex: 1;
        }

        .event-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          margin-bottom: 1rem;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }

        .event-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 0.5rem;
        }

        .event-date {
          font-size: 0.9rem;
          color: #667eea;
          font-weight: 600;
          background: rgba(102, 126, 234, 0.1);
          padding: 0.25rem 0.75rem;
          border-radius: 50px;
          display: inline-block;
          margin-bottom: 1rem;
        }

        .event-description {
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 1.5rem;
        }

        .event-badge {
          position: absolute;
          top: 1rem;
          right: 1rem;
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .event-badge.today {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
        }

        .event-badge.upcoming {
          background: rgba(245, 158, 11, 0.1);
          color: #d97706;
        }

        .event-actions {
          display: flex;
          gap: 0.5rem;
        }

        .action-btn {
          width: 40px;
          height: 40px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .edit-btn {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .edit-btn:hover {
          background: rgba(59, 130, 246, 0.2);
          transform: translateY(-2px);
        }

        .delete-btn {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .delete-btn:hover {
          background: rgba(239, 68, 68, 0.2);
          transform: translateY(-2px);
        }

        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
        }

        .loading-content {
          text-align: center;
          color: white;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1rem;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.5rem;
        }

        .empty-description {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .calendar-container {
            padding: 1rem 0.5rem;
          }
          
          .events-grid {
            grid-template-columns: 1fr;
          }
          
          .form-container {
            padding: 2rem 1.5rem;
          }
          
          .event-card {
            padding: 1.5rem;
          }
          
          .action-bar {
            flex-direction: column;
            align-items: stretch;
          }
          
          .form-actions {
            flex-direction: column;
          }
          
          .submit-btn {
            min-width: unset;
          }

          .upcoming-list {
            flex-direction: column;
          }
        }
      `}</style>

      <div className="pulse-element"></div>
      
      <div className="calendar-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
            </svg>
            Academic Schedule
          </div>
          
          <h1 className="main-title">
            <span className="gradient-text">Academic</span> Calendar
          </h1>
          
          <p className="subtitle">
            Stay organized with important dates and upcoming events
          </p>
        </div>

        {/* Upcoming Events Preview */}
        {getUpcomingEvents().length > 0 && (
          <div className="upcoming-events">
            <div className="upcoming-title">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
              </svg>
              Coming Up Soon
            </div>
            <div className="upcoming-list">
              {getUpcomingEvents().map((event, index) => (
                <div key={event._id} className="upcoming-item">
                  {event.title} • {formatEventDate(event.date)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Bar */}
        <div className="action-bar">
          <div className="events-count">
            {events.length} {events.length === 1 ? 'Event' : 'Events'} Scheduled
          </div>
          
          {role === 'teacher' && (
            <button 
              className="add-event-btn"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
              </svg>
              {showAddForm ? 'Cancel' : 'Add Event'}
            </button>
          )}
        </div>

        {/* Add/Edit Event Form */}
        {role === 'teacher' && showAddForm && (
          <div className="form-container">
            <div className="form-title">
              <div className="form-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                </svg>
              </div>
              {editMode ? 'Edit Event' : 'Schedule New Event'}
            </div>
            
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Event Title *</label>
                <input
                  type="text"
                  name="title"
                  className="form-input"
                  placeholder="e.g. Final Examinations, Sports Day, Parent-Teacher Meeting"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Event Date *</label>
                <input
                  type="date"
                  name="date"
                  className="form-input"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-input form-textarea"
                  placeholder="Add additional details about this event..."
                  value={formData.description}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-actions">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <div className="loading-spinner" style={{width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid white'}}></div>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                    </svg>
                  )}
                  {editMode ? 'Update Event' : 'Schedule Event'}
                </button>
                
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditMode(false);
                    setFormData({ title: '', date: '', description: '' });
                    setEditEventId(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Events Display */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-content">
              <div className="loading-spinner"></div>
              <p>Loading events...</p>
            </div>
          </div>
        ) : events.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
              </svg>
            </div>
            <h3 className="empty-title">No Events Scheduled</h3>
            <p className="empty-description">
              There are currently no events on the calendar. Check back later for updates.
            </p>
          </div>
        ) : (
          <div className="events-grid">
            {events.map((event, index) => (
              <div 
                key={event._id || event.id} 
                className={`event-card ${isEventToday(event.date) ? 'today' : isEventUpcoming(event.date) ? 'upcoming' : ''}`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                {isEventToday(event.date) && (
                  <div className="event-badge today">Today</div>
                )}
                {isEventUpcoming(event.date) && !isEventToday(event.date) && (
                  <div className="event-badge upcoming">Soon</div>
                )}
                
                <div className="event-header">
                  <div className="event-main">
                    <div className="event-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,19H5V8H19M16,1V3H8V1H6V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H18V1M17,12H12V17H17V12Z"/>
                      </svg>
                    </div>
                    <h3 className="event-title">{event.title}</h3>
                    <div className="event-date">{formatEventDate(event.date)}</div>
                  </div>
                  
                  {role === 'teacher' && (
                    <div className="event-actions">
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(event)}
                        title="Edit event"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z"/>
                        </svg>
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(event._id || event.id)}
                        title="Delete event"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z"/>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                {event.description && (
                  <div className="event-description">
                    {event.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Calendar;